import React, { useEffect, useRef, useState } from "react"
import { Input, Tooltip } from "antd"

export interface DraftInputProps {
  /** The stored text. */
  value: string
  /**
   * Writes the text typed. Returns why it was refused, or nothing once it is
   * written; a refused text stays in the field with the reason (see
   * `errorDisplay`).
   */
  onCommit: (text: string) => string | null | void
  ariaLabel: string
  placeholder?: string
  className?: string
  /** A text area: it writes on leaving only, as Enter starts a new line. */
  multiline?: boolean
  /**
   * Where a refusal is said: "below" the field (the default), or in a
   * "tooltip" open on the field while it is in error, for a field in a row
   * of fixed height; screen readers get it from a hidden alert either way.
   */
  errorDisplay?: "below" | "tooltip"
}

/**
 * A text field that keeps what is typed to itself and writes it when the
 * field is left or Enter is pressed, so a rule applied on writing (a rename
 * equal to the label is dropped, an empty text removes the field) never
 * changes the text half way through typing. What is still typed when the
 * field goes away is written then, unless it was refused.
 */
const DraftInput: React.FC<DraftInputProps> = ({
  value,
  onCommit,
  ariaLabel,
  placeholder,
  className,
  multiline = false,
  errorDisplay = "below",
}) => {
  /** The text being typed; null while the field shows the stored text. */
  const [draft, setDraft] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // What the field holds after each render, for the write when it goes away.
  const latest = useRef({ draft, value, onCommit, error })
  useEffect(() => {
    latest.current = { draft, value, onCommit, error }
  })

  const commit = (): void => {
    if (draft === null) return
    if (draft === value) {
      setDraft(null)
      setError(null)
      return
    }
    const refused = onCommit(draft)
    if (typeof refused === "string") {
      setError(refused)
      return
    }
    setDraft(null)
    setError(null)
  }

  // A refused text is never written by itself: only what is still typed and
  // not yet refused.
  useEffect(
    () => () => {
      const { draft: pending, value: stored, onCommit: write, error: refused } = latest.current
      if (pending !== null && pending !== stored && refused === null) write(pending)
    },
    []
  )

  const common = {
    "aria-label": ariaLabel,
    placeholder,
    value: draft ?? value,
    status: error ? ("error" as const) : undefined,
    onBlur: commit,
  }
  const field = multiline ? (
    <Input.TextArea
      {...common}
      autoSize={{ minRows: 3, maxRows: 8 }}
      onChange={(e) => {
        setDraft(e.target.value)
        setError(null)
      }}
    />
  ) : (
    <Input
      {...common}
      size="small"
      onChange={(e) => {
        setDraft(e.target.value)
        setError(null)
      }}
      onPressEnter={commit}
    />
  )
  return (
    <div className={`ts-draft${className ? ` ${className}` : ""}`}>
      {errorDisplay === "tooltip" ? (
        <>
          {/* Open while the field is in error: the row keeps its height. */}
          <Tooltip open={error !== null} title={error} placement="bottomLeft">
            {field}
          </Tooltip>
          {error && (
            <span className="sr-only" role="alert">
              {error}
            </span>
          )}
        </>
      ) : (
        <>
          {field}
          {error && (
            <p className="ts-error" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default DraftInput
